import LegalPage, { Section, P, UL, Highlight, Placeholder } from "../components/LegalPage";

export default function TermsPage() {
  return (
    <LegalPage title="Terms & Conditions" lastUpdated="October 2026" badge="Legal">

      <Highlight>
        Please read these Terms carefully before using Who's On?. By creating an account or using the service,
        you agree to be bound by these Terms. If you do not agree, please do not use Who's On?.
      </Highlight>

      <Section title="1. Introduction">
        <P>These Terms and Conditions ("Terms") govern your use of the Who's On? platform and service ("Who's On?", "the Service", "we", "us", "our"), a non-commercial hobby project operated by an individual based in Warrington, Cheshire, England, United Kingdom.</P>
        <P>By registering for an account or accessing the Service, you agree to these Terms and our Privacy Policy, which is incorporated by reference.</P>
      </Section>

      <Section title="2. Eligibility">
        <P>Who's On? is an adult platform. You must be at least 18 years of age to use the Service.</P>
        <UL items={[
          "By creating an account, you confirm that you are 18 or older.",
          "If we discover that a user is under 18, we will immediately terminate their account.",
          "We reserve the right to request age verification at any time.",
          "The Service is intended for personal, non-commercial use only.",
        ]} />
      </Section>

      <Section title="3. Your Account">
        <P>To access most features of Who's On?, you must register for an account using either an email address and password, or Google Sign-In.</P>
        <UL items={[
          "You are responsible for maintaining the security of your account credentials.",
          "You must not share your account or allow others to access it.",
          "You must provide accurate information during registration.",
          "Each person may hold only one active account.",
          "You must notify us immediately if you suspect unauthorised access to your account.",
        ]} />
        <P>Your public identity on Who's On? is your chosen alias and avatar. You may not use an alias that impersonates another person, brand, or public figure.</P>
      </Section>

      <Section title="4. The Service — Evening Sessions">
        <P>Who's On? operates as an evening social platform. Public chatrooms are available during defined evening session hours (currently 7:00 PM – 11:00 PM UK local time, subject to change with notice).</P>
        <UL items={[
          "Public chat messages are automatically and permanently deleted at the end of each evening session.",
          "The deletion of messages is by design. You should not use Who's On? to store information you wish to retain.",
          "Friends Rooms (private rooms) operate independently of the daily session schedule unless otherwise stated.",
          "We may change session hours at any time with reasonable notice via the platform.",
        ]} />
      </Section>

      <Section title="5. Acceptable Use">
        <P>You agree to use Who's On? in a manner that is lawful, respectful, and consistent with these Terms. You are responsible for all content you post and all actions taken under your account.</P>
        <P>Specifically, you must not:</P>
        <UL items={[
          "Harass, bully, threaten, stalk, or intimidate any other user.",
          "Post or share content that is hateful, discriminatory, or abusive on the basis of race, ethnicity, religion, gender, sexual orientation, disability, or any other characteristic.",
          "Share sexually explicit, pornographic, or graphic violent content.",
          "Post content that glorifies or promotes self-harm or suicide.",
          "Impersonate any person, organisation, or entity.",
          "Spam, flood chat rooms with repetitive messages, or post unsolicited promotional content.",
          "Attempt to scam, defraud, or deceive other users.",
          "Share personal contact details of other users without their consent.",
          "Use the platform to distribute malware, phishing links, or malicious content.",
          "Attempt to circumvent moderation, bans, or technical restrictions.",
          "Use automated tools, bots, or scripts to interact with the Service.",
          "Use the Service for any unlawful purpose.",
        ]} />
      </Section>

      <Section title="6. Content & Moderation">
        <P>Who's On? operates moderated public chatrooms. We reserve the right to:</P>
        <UL items={[
          "Remove any content that violates these Terms or our Community Guidelines at any time without notice.",
          "Issue warnings, temporary mutes, suspensions, or permanent bans at our discretion.",
          "Review reports submitted by users and take action we consider appropriate.",
          "Cooperate with law enforcement authorities where required by law.",
        ]} />
        <P>Moderation decisions are made by human administrators. You may contact us if you believe a decision was made in error, but we are not obliged to reverse any moderation action.</P>
      </Section>

      <Section title="7. Friends Rooms">
        <P>Who's On? allows users to create private Friends Rooms. By creating or joining a Friends Room, you agree that:</P>
        <UL items={[
          "The creator of a Friends Room is responsible for managing that room appropriately.",
          "All Community Guidelines and these Terms apply equally within Friends Rooms.",
          "We retain the right to review and moderate Friends Rooms if reports are received.",
          "The creator should not invite anyone to a private room without their prior agreement.",
        ]} />
      </Section>

      <Section title="8. Intellectual Property">
        <P>Who's On? and all associated branding, logos, and design are owned by <Placeholder label="COMPANY NAME" /> or its licensors. You may not reproduce, distribute, or create derivative works without our express written permission.</P>
        <P>By posting content on Who's On?, you grant us a non-exclusive, royalty-free licence to display that content within the Service for the duration of the session. As messages are deleted at session end, this licence is brief and limited.</P>
      </Section>

      <Section title="9. Availability & Changes">
        <P>We aim to operate Who's On? reliably, but we make no guarantee of uninterrupted availability. We reserve the right to:</P>
        <UL items={[
          "Modify, suspend, or discontinue any part of the Service at any time.",
          "Change session hours, features, or functionality.",
          "Update these Terms — we will notify users of material changes via the platform.",
        ]} />
        <P>Continued use of the Service after changes to these Terms constitutes acceptance of the revised Terms.</P>
      </Section>

      <Section title="10. Disclaimers">
        <P>Who's On? is provided "as is" without warranties of any kind, express or implied. We do not warrant that the Service will be error-free, uninterrupted, or free from harmful content posted by users.</P>
        <P>Conversations on Who's On? are generated by users, not by us. We are not responsible for the accuracy, legality, or appropriateness of user-generated content.</P>
      </Section>

      <Section title="11. Limitation of Liability">
        <P>To the fullest extent permitted by applicable law, <Placeholder label="COMPANY NAME" /> shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, including (without limitation) loss of data, reputation, or revenue.</P>
        <P>Nothing in these Terms limits our liability for death or personal injury caused by our negligence, fraud, or fraudulent misrepresentation, or any other liability that cannot be excluded by law.</P>
      </Section>

      <Section title="12. Termination">
        <P>We may terminate or suspend your account at any time, without prior notice, if we believe you have violated these Terms.</P>
        <P>You may close your account at any time by contacting us. Upon termination, your account data will be deleted in accordance with our Privacy Policy. Public chat messages are already deleted at session end. Your alias, avatar preferences, and friend connections will be removed.</P>
      </Section>

      <Section title="13. Governing Law">
        <P>These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</P>
      </Section>

      <Section title="14. Contact">
        <P>If you have any questions about these Terms, please contact us at:</P>
        <P>
          <strong className="text-white">WHO'S ON?</strong><br />
          Non-commercial hobby project · Warrington, Cheshire, England<br />
          Email: <a href="mailto:bbz199.bb@gmail.com" className="text-purple-400 hover:text-purple-300 underline">bbz199.bb@gmail.com</a>
        </P>
      </Section>

    </LegalPage>
  );
}
